import React, { useRef } from 'react';
import styled from 'styled-components';
import type { InputHTMLAttributes } from 'react';
import type { FieldGroupProps } from '../formGroup/types';
import type { AllColorType, AllIconType } from '../../util/types';
import { ButtonIcon } from '../';
import FieldGroup from '../formGroup/FieldGroup';
import { DEFAULT_TRANSITION_DURATION, DEFAULT_TRANSITION_FUNC, getFocusStyle } from '../../util/styled';
import { GLOBAL_ICONS } from '../../util/vars';
import { ICON_WRAPPER } from '../../util/data-id';

type Ref = HTMLInputElement;
type HTMLProps = Omit<InputHTMLAttributes<Ref>, 'id' | 'required'>;
interface Props extends HTMLProps, FieldGroupProps {
  id: string;
  label: string;
  icon?: AllIconType | null;
  iconColor?: InputStyledWithIconProps['color'];
  iconPosition?: 'left' | 'right' | 'right-outside';
  onIconClick?: () => void;
  onClear?: () => void;
  className?: string;
}

const Input = React.forwardRef<Ref, Props>(({
  id,
  label,
  hint,
  hideLabel,
  minWidthForLabel = undefined,
  required = false,
  isInline = false,
  icon,
  iconColor,
  iconPosition = 'right',
  labelStyle = null,
  onIconClick,
  onClear,
  error = null,
  className = '',
  disabled = false,
  ...other
}: Props, ref): JSX.Element => {

  const internalRef = useRef<HTMLInputElement | null>(null);

  return (
    <FieldGroup
      id={id}
      required={required}
      label={label}
      hint={hint}
      hideLabel={hideLabel}
      labelStyle={labelStyle}
      minWidthForLabel={minWidthForLabel}
      isInline={isInline}
      error={error}
    >
      <InputStyledWithIcon
        icon={icon}
        color={iconColor}
        iconPosition={iconPosition}
        onIconClick={onIconClick}
        disabled={disabled}
      >
        <>
          <InputStyled
            ref={(legacyRef) => {
              if (null != ref) {
                if ('function' === typeof ref) {
                  ref(legacyRef);
                } else {
                  ref.current = legacyRef;
                }
              }

              internalRef.current = legacyRef;
            }}
            id={id}
            name={other?.name ?? id}
            type={other?.type ?? 'text'}
            disabled={disabled}
            aria-label={label}
            className={`appearance-none w-full bg-gray-100 rounded border-transparent p-2 ${icon ? (iconPosition === 'left' ? (onClear ? 'px-12' : 'pl-12') : (onClear ? 'pr-20' : 'pr-12')) : onClear ? 'pr-12' : ''} ${className}`}
            {...other as HTMLProps}
          />
          { onClear ? (
            <span className={`absolute inset-y-0 flex items-center right-0 ${icon && iconPosition === 'right' ? 'pr-12' : 'pr-2'}`}>
              <ClearIcon
                icon={GLOBAL_ICONS.close}
                onClick={() => {
                  (internalRef.current as HTMLInputElement).value = '';
                  onClear();
                }}
                type={'button'}
                size={'md'}
                color={'transparent'}
                isOutline
                isCircle
              />
            </span>
          ) : null }
        </>
      </InputStyledWithIcon>
    </FieldGroup>
  );
});

interface InputStyledWithIconProps {
  icon?: Props['icon'];
  iconPosition: Props['iconPosition'];
  onIconClick?: Props['onIconClick'];
  children: JSX.Element;
  color?: AllColorType;
  disabled?: boolean;
}

const InputStyledWithIcon = ({ icon, iconPosition, onIconClick, children, color = 'transparent', disabled = false }: InputStyledWithIconProps) => {
  if (!icon) {
    return children;
  }
  return (
    <div className={'relative flex gap-2 w-full'}>
      <span className={`${[ 'right', 'left' ].includes(iconPosition ?? '') ? 'absolute' : ''} ${iconPosition === 'right-outside' ? 'order-last' : ''} inset-y-0 flex items-center ${iconPosition === 'left' ? 'left-0 pl-2' : iconPosition === 'right' ? 'right-0 pr-2' : ''}`}>
        <ButtonIcon
          icon={icon}
          onClick={onIconClick}
          type={'button'}
          size={'sm'}
          color={color}
          isOutline
          isCircle
          disabled={disabled}
        />
      </span>
      {children}
    </div>
  );
};

const InputStyled = styled('input')`
  transition: box-shadow ${DEFAULT_TRANSITION_FUNC} ${DEFAULT_TRANSITION_DURATION};
  &:focus {
    ${(props) => getFocusStyle(props.theme.colors.primary)}
  }
`;

const ClearIcon = styled(ButtonIcon)`
  [data-id='${ICON_WRAPPER}'] {
    width: calc(${(props) => props.theme.sizes[props.size ?? 'md']}/3);
    height: calc(${(props) => props.theme.sizes[props.size ?? 'md']}/3);
  }
`;

export default Input;
